import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../../lib/site";
import { MaskedLines } from "../../lib/animations";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#160c03]"
    >
      {/* warm glow behind the subject */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[85%] w-[55%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(228,120,98,0.28),transparent_65%)] blur-2xl" />

      {/* Dancer — revealed from darkness (white bg blends away via multiply) */}
      <motion.div
        style={{ y: imgY }}
        initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
        animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
        transition={{ duration: 1.8, ease: EASE, delay: 0.5 }}
        className="absolute inset-x-0 bottom-0 top-0 flex items-end justify-center"
      >
        <motion.img
          style={{ scale: imgScale }}
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: EASE, delay: 0.4 }}
          src="/hero-dancer.png"
          alt="Cibele performing"
          className="h-[92%] w-auto max-w-none object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.55)] md:h-[98%]"
        />
      </motion.div>

      {/* Falling veil overlay */}
      <motion.div
        initial={{ y: "0%", opacity: 1 }}
        animate={{ y: "-115%", opacity: 0 }}
        transition={{ duration: 1.9, ease: [0.7, 0, 0.3, 1], delay: 0.25 }}
        className="pointer-events-none absolute inset-0 z-30"
      >
        <div className="h-full w-full bg-gradient-to-b from-[#E4483A] via-[#c02f2f]/90 to-[#7a1220]/80 backdrop-blur-sm" />
      </motion.div>
      <motion.div
        initial={{ y: "0%" }}
        animate={{ y: "-125%" }}
        transition={{ duration: 2.1, ease: [0.7, 0, 0.3, 1], delay: 0.35 }}
        className="pointer-events-none absolute inset-0 z-20"
      >
        <div className="h-full w-full bg-gradient-to-b from-[#8a1728] to-transparent opacity-70" />
      </motion.div>

      {/* Vignette + bottom fade for text legibility */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(10,6,2,0.75)_100%)]"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/2 bg-gradient-to-t from-[#160c03] via-[#160c03]/50 to-transparent" />

      {/* Text overlay */}
      <div className="relative z-40 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-6 pb-24 md:px-12 md:justify-center md:pb-0">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mb-6 font-sans text-[0.7rem] uppercase tracking-[0.42em] text-peach/90"
          >
            Amsterdam · Athens · Cairo · Istanbul
          </motion.p>

          <h1 className="font-serif font-light leading-[1.14] tracking-tight text-champagne text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
            <MaskedLines lines={["Cibele is a bellydancer,"]} delay={0.9} />
            <MaskedLines
              lines={["oriental explorer, writer"]}
              lineClassName="italic text-blush"
              delay={1.02}
            />
            <MaskedLines lines={["and entrepreneur."]} delay={1.14} />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-book-btn"
              onClick={() => scrollToId("#entertainment")}
              className="group inline-flex items-center gap-3 rounded-full bg-peach px-9 py-4 font-sans text-xs uppercase tracking-[0.26em] text-espresso transition-colors duration-300 hover:bg-champagne"
            >
              See the Show
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              data-testid="hero-story-btn"
              onClick={() => scrollToId("#about")}
              className="font-sans text-xs uppercase tracking-[0.26em] text-champagne/70 underline-offset-8 transition-colors duration-300 hover:text-blush hover:underline"
            >
              Discover the story
            </button>
          </motion.div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-40 -translate-x-1/2 font-sans text-[0.6rem] uppercase tracking-[0.3em] text-champagne/50"
      >
        <span className="animate-pulse">Scroll</span>
      </motion.div>
    </section>
  );
};
